import MessagesClient from '@/client/mentor/MessagesClient'
import type {CursorResponse} from '@/client/mentor/types'
import type {InfiniteData} from '@tanstack/vue-query'
import type {MaybeRef} from 'vue'
import type {Maybe} from '@/types'
import type {Message} from '@/client/mentor/MessagesClient'
import {MESSAGES_PAGE_SIZE} from '@/components/assistant/constants'
import {PaginationDirection} from '@/client/mentor/types'
import {computed, toValue} from 'vue'
import {useInfiniteQuery, useMutation, useQueryClient} from '@tanstack/vue-query'

export type MessagePage = CursorResponse<Message, number>
export type MessagesInfiniteData = InfiniteData<MessagePage, number | undefined>

export function useAssistantMessages(
  companyId: MaybeRef<number>,
  conversationId: MaybeRef<Maybe<string>>,
  options?: {
    enabled?: MaybeRef<boolean>
  }
) {
  const queryClient = useQueryClient()
  const resolvedCompanyId = computed(() => toValue(companyId))
  const resolvedConversationId = computed(() => toValue(conversationId))
  const queryKey = computed(() => buildMessageQueryKey(resolvedCompanyId.value, resolvedConversationId.value))

  const isEnabledByCaller = computed(() => toValue(options?.enabled) ?? true)
  const queryEnabled = computed(() => isEnabledByCaller.value && Boolean(resolvedConversationId.value))

  const messagesQuery = useInfiniteQuery({
    queryKey,
    enabled: queryEnabled,
    initialPageParam: undefined as number | undefined,
    queryFn: async ({pageParam}) => {
      return MessagesClient.getMessages(resolvedCompanyId.value, resolvedConversationId.value as string, {
        cursor: pageParam,
        limit: MESSAGES_PAGE_SIZE,
        direction: PaginationDirection.DOWN,
      })
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: 5 * 60 * 1_000,
  })

  const messages = computed(() => messagesQuery.data.value?.pages.flatMap((page) => page.items) ?? [])

  const updateMessageMutation = useMutation({
    mutationFn: async (messageId: number) => {
      return MessagesClient.getMessage(resolvedCompanyId.value, resolvedConversationId.value as string, messageId)
    },
    onSuccess: (updatedMessage) => {
      queryClient.setQueryData<MessagesInfiniteData | undefined>(queryKey.value, (oldData) => {
        if (!oldData) return oldData

        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            items: page.items.map((item) => (item.id === updatedMessage.id ? updatedMessage : item)),
          })),
        }
      })
    },
  })

  return {
    messages,
    isLoading: messagesQuery.isLoading,
    isFetchingNextPage: messagesQuery.isFetchingNextPage,
    hasNextPage: messagesQuery.hasNextPage,
    fetchNextPage: async () => {
      await messagesQuery.fetchNextPage()
    },
    updateMessageById: updateMessageMutation.mutateAsync,
  }
}

export function buildMessageQueryKey(companyId: number, conversationId: Maybe<string>) {
  return ['mentor', 'conversations', companyId, 'message', conversationId]
}
